import * as React from 'react';
import { ls } from '../../i18n/translations';
import { dayjs, isModuleInAlarm, parseMachineType, parseMachineVersion } from '../../helpers/utils';
import { PageName } from '../../routes';
import { Link } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import Center from '../ui/Center';
import { colors } from '../../theme';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { defaultMUIDataTableOptions } from '../../helpers/datatableUtils';
import { Device, MachineType, MachineVersion, Module } from '../../graphql/codegen/graphql-react';
import DeviceIconStatus from '../DeviceIconStatus';
import { useDarkMode } from '../../hook/useDarkMode';
import { useAuth } from '../../hook/useAuth';

const options: MUIDataTableOptions = {
  ...defaultMUIDataTableOptions,
};

type ModuleRow = {
  deviceId: string;
  moduleName: string;
  mac: string;
  moduleType: string;
  moduleVersion: string;
  userEmail: string;
  company: string;
};

interface ModulesManagementTableProps {
  devices: Device[];
}

const buildRows = (devices: Device[]): ModuleRow[] => {
  const rows: ModuleRow[] = [];
  (devices || []).forEach((d) => {
    const deviceId = d?.id || '';
    (d?.modules || []).forEach((m, i) => {
      if (!m) {
        return;
      }
      const isOnline = Boolean(m.online);
      rows.push({
        deviceId: deviceId || `${i}`,
        moduleName: m.name || '',
        mac: m.mac || '',
        moduleType: m.type ? parseMachineType(m.type) : parseMachineType(MachineType.Unknown),
        moduleVersion: m.modelVersion
          ? parseMachineVersion(m.modelVersion)
          : parseMachineVersion(MachineVersion.Unknown),
        userEmail: d?.User?.email || '',
        company: d?.Company?.name || '',
      });
    });
  });
  return rows;
};

const ModulesManagementTable: React.FC<ModulesManagementTableProps> = ({ devices }) => {
  const { isDarkMode } = useDarkMode();
  const { isSuperAdmin } = useAuth();
  const dateTimeFile = dayjs().format('YYYY-MM-DD_HH_mm');
  const localStorageRowsPerPageKey = 'modulesmanagement_rowsPerPage';

  const tableModules = React.useMemo(() => buildRows(devices), [devices]);

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'moduleName',
      label: ls.instrumentName.toUpperCase(),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'mac',
      label: ls.macAddress.toUpperCase(),
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: 'nowrap',
          },
        }),
      },
    },
    {
      name: 'moduleType',
      label: ls.tool.toUpperCase(),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'moduleVersion',
      label: ls.version.toUpperCase(),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'userEmail',
      label: ls.user.toUpperCase(),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'company',
      label: ls.company.toUpperCase(),
      options: {
        filter: true,
        sort: true,
        display: isSuperAdmin,
      },
    },
    {
      name: 'deviceId',
      label: ls.details.toUpperCase(),
      options: {
        filter: false,
        sort: false,
        searchable: false,
        download: false,
        customBodyRender: (value) => {
          const editPath = PageName.editDevice.replace('{0}', value);
          const iconColor = isDarkMode ? colors.lightGrey : colors.blue;
          return (
            <Center justifyContent={'flex-start'}>
              <Tooltip title={ls.modifyDevice}>
                <IconButton
                  component={Link}
                  to={editPath}
                  size='small'
                  aria-label={ls.modifyDevice}
                  sx={{ color: iconColor }}
                >
                  <Edit fontSize='small' />
                </IconButton>
              </Tooltip>
            </Center>
          );
        },
      },
    },
  ];

  return (
    <MUIDataTable
      title=''
      data={tableModules}
      columns={columns}
      options={{
        ...options,
        downloadOptions: {
          filename: `Modules_${dateTimeFile}.csv`,
        },
        sortOrder: {
          name: 'status',
          direction: 'asc',
        },
        rowsPerPage: localStorage.getItem(localStorageRowsPerPageKey)
          ? Number(localStorage.getItem(localStorageRowsPerPageKey))
          : 10,
        onChangeRowsPerPage: (numberOfRows) => {
          localStorage.setItem(localStorageRowsPerPageKey, `${numberOfRows}`);
        },
      }}
    />
  );
};

export default ModulesManagementTable;
